import { OptimizedImage } from '@/components/optimized-image';
import { getMediaUrl } from '@/lib/media';
import { formatRating } from '@/lib/rating';
import { type ListingStockProfile } from '@/types';
import { Link } from '@inertiajs/react';
import { Star } from 'lucide-react';

interface ListingStockHeaderProps {
    profile: ListingStockProfile;
}

/**
 * Faixa compacta que identifica a empresa no topo do estoque. No celular cabe em ~56px
 * para não empurrar os cards para fora da primeira tela. Sem contato direto: o lead
 * nasce sempre de um anúncio.
 */
export function ListingStockHeader({ profile }: ListingStockHeaderProps) {
    const logoUrl = getMediaUrl(profile.logo, 'logo');
    const location = [profile.city, profile.state_code].filter(Boolean).join(', ');
    const { rating } = profile;

    return (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            {logoUrl && (
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-white">
                    <OptimizedImage
                        src={logoUrl}
                        alt={`Logo de ${profile.title}`}
                        className="h-10 w-10 object-contain"
                        priority={true}
                        loading="eager"
                        width={40}
                        height={40}
                        sizes="40px"
                    />
                </div>
            )}

            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{profile.title}</p>

                {(rating || location) && (
                    <p className="flex min-w-0 items-center gap-1 text-xs text-gray-600">
                        {rating && (
                            <>
                                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" aria-hidden="true" />
                                <span className="shrink-0 whitespace-nowrap">
                                    <span className="font-medium text-gray-800">{formatRating(rating.average)}</span> ({rating.count}{' '}
                                    {rating.count === 1 ? 'avaliação' : 'avaliações'})
                                </span>
                            </>
                        )}
                        {rating && location && <span aria-hidden="true">·</span>}
                        {location && <span className="min-w-0 truncate">{location}</span>}
                    </p>
                )}
            </div>

            <Link
                href={profile.profile_path}
                className="inline-flex min-h-11 shrink-0 items-center px-1 text-sm font-medium text-primary hover:underline"
            >
                Ver perfil
            </Link>
        </div>
    );
}
